import { Table, Badge, Group, Text, ActionIcon, Progress } from '@mantine/core'
import { IconExternalLink, IconPower } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import type { Node } from '@/types'
import { formatBytes } from '@/utils/format'

interface NodeTableProps {
  nodes: Node[]
  loading?: boolean
}

export function NodeTable({ nodes, loading }: NodeTableProps) {
  const navigate = useNavigate()

  const rows = nodes.map((node) => {
    const cpuCores = node.Description.Resources.NanoCPUs / 1_000_000_000
    const memory = formatBytes(node.Description.Resources.MemoryBytes)
    
    return (
      <Table.Tr key={node.ID}>
        <Table.Td>
          <Group gap="xs">
            <Text fw={500}>{node.Description.Hostname}</Text>
            {node.ManagerStatus && (
              <Badge size="xs" variant="light" color="blue">
                Manager
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Badge
            color={node.Status.State === 'ready' ? 'green' : 'red'}
            variant="dot"
          >
            {node.Status.State}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{node.Spec.Availability}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{cpuCores} CPUs</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{memory}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{node.Description.Platform.OS}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{node.Status.Addr}</Text>
        </Table.Td>
        <Table.Td>
          <Group gap="xs" justify="flex-end">
            <ActionIcon
              variant="subtle"
              onClick={() => navigate(`/nodes/${node.ID}`)}
              title="View details"
            >
              <IconExternalLink size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="orange"
              title="Drain node"
            >
              <IconPower size={16} />
            </ActionIcon>
          </Group>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <Table horizontalSpacing="md" verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Hostname</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Availability</Table.Th>
          <Table.Th>CPU</Table.Th>
          <Table.Th>Memory</Table.Th>
          <Table.Th>OS</Table.Th>
          <Table.Th>Address</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>{rows}</Table.Tbody>
    </Table>
  )
}